import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { projects, type Project } from '../data/portfolioProjects';
import { ProjectFrame } from '../components/portfolio/ProjectFrame';
const filters: Array<Project['type'] | 'All work'> = ['All work', 'Custom build', 'Redesign', 'Ecommerce', 'Integration'];
export function Portfolio() {
  const [filter, setFilter] = useState<(typeof filters)[number]>('All work');
  const shown = filter === 'All work' ? projects : projects.filter(p => p.type === filter);
  return <>
      <section className="relative overflow-hidden bg-ink pb-14 pt-14 lg:pb-16 lg:pt-20" data-id="element-505" data-mp="33l91odqly1ti">
        <div className="absolute inset-0 bp opacity-80" aria-hidden="true" data-id="element-506" data-mp="2zlql1u5tkcwh" />
        <div className="absolute -right-40 -top-32 h-[36rem] w-[36rem] rounded-full bg-navy-700/35 blur-[130px]" aria-hidden="true" data-id="element-507" data-mp="197zvautcywfh" />

        <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-508" data-mp="157zckxz87z2m">
          <p className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.24em] text-forge-cyan" data-id="element-509" data-mp="16avehjji0rh5">
            <span className="h-px w-7 bg-forge-orange" aria-hidden="true" data-id="element-510" data-mp="1b9yjj3zhaxp4" />
            Portfolio
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-[2rem] font-extrabold leading-[1.08] tracking-[-0.02em] text-white sm:text-[2.7rem]" data-id="element-511" data-mp="1g6p0run0rzc4">
            Work across industries that have nothing in common
            <span className="block text-forge-orange" data-id="element-512" data-mp="1pxte6px1rc4u">except that a template could not do the job.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-mist-200" data-id="element-513" data-mp="1u1izn6ifddln">
            Apparel and printing, bathroom remodeling, martial arts, auto service, marine, industrial, chiropractic, retail and
            ecommerce, fitness. Each build starts with how the business runs, so no two structures look the same.
          </p>

          <div className="mt-9 flex flex-wrap gap-2" role="group" aria-label="Filter work by type" data-id="element-514" data-mp="9j1lebvzo2v0">
            {filters.map(f => <button key={f} type="button" onClick={() => setFilter(f)} aria-pressed={filter === f} className={`rounded-xl border px-4 py-2.5 text-[13px] font-semibold transition-colors ${filter === f ? 'border-forge-orange bg-forge-orange/15 text-white' : 'border-white/12 bg-white/[0.03] text-mist-200 hover:border-white/30'}`} data-id="element-515" data-mp="1jf478m9p3ve">
                {f}
              </button>)}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy-900 py-14 lg:py-16" data-id="element-516" data-mp="u713w5sdjocz">
        <div className="absolute inset-0 dots opacity-[0.25]" aria-hidden="true" data-id="element-517" data-mp="pa12472k57ro" />
        <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-518" data-mp="1471tg9gu70pm">
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-519" data-mp="z7fftyx3jiga">
            {shown.map((project, i) => <motion.article key={project.industry} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: i * 0.04
          }} className="group overflow-hidden rounded-2xl border border-white/10 bg-navy-950/70 transition-colors hover:border-white/25" data-id="element-520" data-mp="1isfosadqe44d" data-mpi={i}>
                <ProjectFrame project={project} data-id="element-521" data-mp="z46jqzr3a3ng" />
                <div className="p-6" data-id="element-522" data-mp="z3ez2jz15qby">
                  <div className="flex items-center justify-between gap-3" data-id="element-523" data-mp="13v6ga3waqjpf">
                    <h2 className="font-display text-[17px] font-bold text-white" data-id="element-524" data-mp="13xj42xoqjoae">{project.industry}</h2>
                    <span className="rounded-full border border-white/12 bg-white/[0.04] px-2.5 py-1 font-mono text-[9.5px] uppercase tracking-[0.14em] text-mist-300" data-id="element-525" data-mp="q272q7g2qzki">
                      {project.type}
                    </span>
                  </div>
                  <p className="mt-2.5 text-[12.5px] leading-relaxed text-mist-400" data-id="element-526" data-mp="l3m0k2h9i5tf">{project.scope}</p>
                  <p className="mt-3.5 border-t border-white/10 pt-3.5 text-[13.5px] leading-relaxed text-mist-100" data-id="element-527" data-mp="1oejek9egmlsv">
                    {project.outcome}
                  </p>
                </div>
              </motion.article>)}
          </div>

          <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl bg-ink px-6 py-8 sm:flex-row sm:items-center sm:justify-between lg:px-9" data-id="element-528" data-mp="19r80o0ut2ppl">
            <p className="max-w-2xl font-display text-[17px] font-bold leading-snug text-white" data-id="element-529" data-mp="14tewtegz67sh">
              Want to see live client sites and the detail behind a specific build? Ask us and we will walk you through it.
            </p>
            <Link to="/#get-started" className="group inline-flex shrink-0 items-center gap-2 rounded-xl bg-forge-orange px-5 py-3.5 text-[14.5px] font-bold text-ink transition-transform hover:-translate-y-0.5" data-id="element-530" data-mp="qs2it8ldkg98">
              Get Started
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-531" data-mp="2l40q3504j22h" />
            </Link>
          </div>
        </div>
      </section>
    </>;
}